/**
 * REST client for the checklist-item scan feature. Same auth posture as the
 * agent transport: the raw access token is sent as the Authorization header
 * (no "Bearer " prefix). All paths are relative in dev so the Vite proxy
 * forwards them to the backend (see vite.config.ts and shared/config/endpoints.ts).
 */
import { aiApiBaseUrl, apiBaseUrl, searchApiBaseUrl } from '@/shared/config/endpoints'
import { getStoredAuth } from '@/shared/auth/login'
import type {
    ChecklistItem,
    DocumentScanResponse,
    ScanHighlights,
    ScanResult,
    ScanTextBlock,
    ScanVerdict,
    WordDocumentMeta
} from './types'

/** HTTP error carrying the status code so callers can detect auth failures. */
export class ScanHttpError extends Error {
  statusCode: number
  constructor(statusCode: number, message: string) {
    super(message)
    this.name = 'ScanHttpError'
    this.statusCode = statusCode
  }
}

function authHeaders(): Record<string, string> {
  const token = getStoredAuth()?.token
  return token ? { Authorization: token } : {}
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(init.headers as Record<string, string> | undefined)
    }
  })
  if (!res.ok) {
    throw new ScanHttpError(res.status, await safeErrorMessage(res))
  }
  if (res.status === 204) return undefined as T
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

async function safeErrorMessage(res: Response): Promise<string> {
  try {
    const data = await res.json()
    return data?.message || data?.error?.message || `Request failed (${res.status}).`
  } catch {
    return `Request failed (${res.status}).`
  }
}

/**
 * List checklist items for the picker. Filters use the same shape as the web
 * app (`{ field, comparisonOperator, value }`); callers pass whatever filters
 * apply (e.g. the archived scope) — see useChecklistScan.
 */
export async function searchChecklistItems(
  filters: Array<Record<string, unknown>> = []
): Promise<ChecklistItem[]> {
  // The V2 search-service validates `archived` as a BOOLEAN with operator 'eq'
  // (not the string 'false'); `limit` max is 1000 — fetch the full list in one
  // page since the picker filters the rest client-side. Mirrors ramp-web-app.
  const body = JSON.stringify({ filters, limit: 1000 })
  const res = await request<{ items?: ChecklistItem[] }>(`${searchApiBaseUrl}/checklist-items`, {
    method: 'POST',
    body
  })
  return res?.items ?? []
}

/** Insurance group row used to populate the Insurance Groups multi-select. */
export interface TypeOfInsuranceGroup {
  _id: string
  name: string
  businessType?: string
}

/**
 * Active insurance groups for the Filters panel. Same endpoint/params as
 * ramp-web-app's ChecklistItemSelectorDialog (`GET /type-of-insurance-groups`).
 */
export async function getTypeOfInsuranceGroups(): Promise<TypeOfInsuranceGroup[]> {
  const params = new URLSearchParams({ archived: 'false', limit: '1000', offset: '0' })
  const res = await request<TypeOfInsuranceGroup[] | { items?: TypeOfInsuranceGroup[] }>(
    `${apiBaseUrl}/type-of-insurance-groups?${params}`,
    { method: 'GET' }
  )
  return Array.isArray(res) ? res : (res?.items ?? [])
}

/**
 * Dispatch a document scan over the live Word text. Reuses the shared single-scan
 * endpoint with the `WORD` source: the document text is sent inline (no PDF, no
 * S3) as a single entry, fanned out across the selected checklist items, and the
 * open document's identity rides along as `metadata.word` so the resulting rows
 * can be filtered by document. Returns the scan id + seeded INITIAL rows to poll.
 */
export async function createDocumentScan(payload: {
  checklistItemIds: string[]
  text: string
  metadata?: WordDocumentMeta
}): Promise<DocumentScanResponse> {
  const { checklistItemIds, text, metadata } = payload
  return request<DocumentScanResponse>(`${aiApiBaseUrl}/attachments/single-scan`, {
    method: 'POST',
    body: JSON.stringify({
      source: 'WORD',
      checklistItemIds,
      entries: [{ text, ...(metadata ? { metadata: { word: metadata } } : {}) }]
    })
  })
}

/**
 * Rescan a single resolved row against the current document. WORD rows persist
 * no text, so the live document text is re-sent; the row flips back to INITIAL
 * and the caller resumes polling it by its scan id.
 */
export async function rescanDocumentScanResult(checklistItemScanResultId: string, text: string): Promise<void> {
  await request<void>(`${aiApiBaseUrl}/attachments/single-rescan`, {
    method: 'POST',
    body: JSON.stringify({ checklistItemScanResultId, text })
  })
}

/**
 * All WORD scan-result rows for the open document, newest first — the history
 * shown when the pane opens. Scoped by the document's path (its unique key),
 * falling back to the name for unsaved documents. Returns [] when neither is
 * known. Names aren't projected here (the search can't embed checklist items);
 * the caller resolves them from the loaded picker list.
 */
const HISTORY_LIMIT = 500

export async function searchDocumentScanResults(meta: {
  documentPath?: string
  documentName?: string
}): Promise<ScanResult[]> {
  const scope = meta.documentPath
    ? { field: 'metadata.documentPaths', comparisonOperator: 'in', value: [meta.documentPath] }
    : meta.documentName
      ? { field: 'metadata.documentNames', comparisonOperator: 'in', value: [meta.documentName] }
      : null
  if (!scope) return []
  const res = await request<{ items?: ScanResult[] }>(
    `${aiApiBaseUrl}/checklist-item-scan-results/search`,
    {
      method: 'POST',
      body: JSON.stringify({
        filters: [{ field: 'sources', comparisonOperator: 'in', value: ['WORD'] }, scope],
        fields: ['_id', 'checklistItemId', 'checklistItemScanId', 'status', 'scanStatus', 'createdAt', 'updatedAt'],
        limit: HISTORY_LIMIT,
        sortKey: '_id',
        sortDirection: -1
      })
    }
  )
  return res?.items ?? []
}

/** Poll the status/verdict of scan-result rows for the given scan ids. */
export async function searchScanResults(scanIds: string[]): Promise<ScanResult[]> {
  if (!scanIds.length) return []
  const res = await request<{ items?: ScanResult[] }>(
    `${aiApiBaseUrl}/checklist-item-scan-results/search`,
    {
      method: 'POST',
      body: JSON.stringify({
        filters: [{ field: 'checklistItemScanIds', comparisonOperator: 'in', value: scanIds }],
        fields: ['_id', 'checklistItemId', 'checklistItemScanId', 'status', 'scanStatus', 'createdAt', 'updatedAt'],
        limit: 2000,
        sortKey: '_id',
        sortDirection: -1
      })
    }
  )
  return res?.items ?? []
}

/**
 * Raw highlights row as returned by the backend: `scanStatus` is top-level and
 * `{ textBlocks, explanation }` live under `highlights` (projection in
 * checklist-item-scan-results.service.js). We normalize to ScanHighlights.
 */
interface RawHighlightsRow {
  _id: string
  scanStatus?: ScanVerdict
  highlights?: { textBlocks?: ScanTextBlock[]; explanation?: string }
}

/** Fetch highlight text blocks for resolved scan-result rows. */
export async function getScanResultsHighlights(ids: string[]): Promise<ScanHighlights[]> {
  if (!ids.length) return []
  const query = ids.map((id) => `ids=${encodeURIComponent(id)}`).join('&')
  const res = await request<RawHighlightsRow[]>(
    `${aiApiBaseUrl}/checklist-item-scan-results/highlights?${query}`,
    { method: 'GET' }
  )
  if (!Array.isArray(res)) return []
  return res.map((row) => ({
    _id: row._id,
    scanStatus: row.scanStatus,
    explanation: row.highlights?.explanation,
    textBlocks: row.highlights?.textBlocks
  }))
}
